import { useState, useEffect, useRef } from 'react'
import { Phone, ArrowRight, RefreshCw, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '../store/authStore'

export default function OTPVerification({ phoneNumber, onVerified, onCancel }) {
  const { setupRecaptcha, sendPhoneOTP, verifyPhoneOTP } = useAuthStore()
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [isVerifying, setIsVerifying] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [timeLeft, setTimeLeft] = useState(300) // 5 minutos
  const [canResend, setCanResend] = useState(false)
  const recaptchaContainerRef = useRef(null)
  const inputRefs = useRef([])

  // Configurar reCAPTCHA y enviar OTP al montar
  useEffect(() => {
    initializePhoneVerification()
    
    return () => {
      // Cleanup si es necesario
    }
  }, [])

  // Contador regresivo
  useEffect(() => {
    if (timeLeft <= 0) {
      setCanResend(true)
      return
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1)
    }, 1000)

    return () => clearInterval(timer)
  }, [timeLeft])

  const initializePhoneVerification = async () => {
    setIsSending(true)
    
    // Setup reCAPTCHA
    const verifier = setupRecaptcha('recaptcha-container')
    
    if (!verifier) {
      toast.error('Error al configurar verificación')
      setIsSending(false)
      return
    }

    // Enviar OTP
    const result = await sendPhoneOTP(phoneNumber)
    setIsSending(false)
    
    if (!result.success) {
      // El error ya se mostró en sendPhoneOTP
      setTimeout(() => onCancel(), 2000)
    }
  }

  const handleResend = async () => {
    if (!canResend) return
    
    setIsSending(true)
    setCanResend(false)
    setTimeLeft(300)
    setOtp(['', '', '', '', '', ''])
    
    const result = await sendPhoneOTP(phoneNumber)
    setIsSending(false)
    
    if (!result.success) {
      setCanResend(true)
    }
  }

  const handleVerify = async () => {
    const code = otp.join('')
    
    if (code.length !== 6) {
      toast.error('Por favor ingresa el código completo')
      return
    }

    setIsVerifying(true)
    
    const result = await verifyPhoneOTP(code)
    
    if (result.success) {
      onVerified()
    } else {
      // Limpiar inputs en caso de error
      setOtp(['', '', '', '', '', ''])
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus()
      }
    }
    
    setIsVerifying(false)
  }

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return

    const newOtp = [...otp]
    newOtp[index] = value

    setOtp(newOtp)

    // Auto-focus al siguiente input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    // Auto-verificar cuando se complete
    if (index === 5 && value && newOtp.every(digit => digit)) {
      setTimeout(() => {
        handleVerify()
      }, 100)
    }
  }

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    const newOtp = pastedData.split('')
    
    while (newOtp.length < 6) {
      newOtp.push('')
    }
    
    setOtp(newOtp)
    
    if (newOtp.every(digit => digit)) {
      setTimeout(() => {
        handleVerify()
      }, 100)
    }
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-primary-100 p-3 rounded-full">
                <Phone className="text-primary-600" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Verificar Teléfono</h3>
                <p className="text-sm text-gray-600">+52 {phoneNumber}</p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isVerifying || isSending}
            >
              <X size={24} />
            </button>
          </div>

          <p className="text-gray-600 mb-6">
            Ingresa el código de 6 dígitos que enviamos por SMS
          </p>

          {/* reCAPTCHA Container (invisible) */}
          <div id="recaptcha-container" ref={recaptchaContainerRef}></div>

          {/* OTP Inputs */}
          <div className="flex gap-2 justify-center mb-6">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength="1"
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-primary-600 focus:outline-none transition-colors text-gray-900 bg-white"
                disabled={isVerifying || isSending}
              />
            ))}
          </div>

          {/* Timer */}
          <div className="text-center mb-6">
            {timeLeft > 0 ? (
              <p className="text-sm text-gray-600">
                Código válido por: <span className="font-semibold text-primary-600">{formatTime(timeLeft)}</span>
              </p>
            ) : (
              <p className="text-sm text-red-600">
                El código ha expirado. Solicita uno nuevo.
              </p>
            )}
          </div>

          {/* Verify Button */}
          <button
            onClick={handleVerify}
            disabled={otp.some(digit => !digit) || isVerifying || isSending}
            className="w-full btn btn-primary mb-4 flex items-center justify-center gap-2"
          >
            {isVerifying ? (
              <>
                <RefreshCw className="animate-spin" size={20} />
                Verificando...
              </>
            ) : (
              <>
                Verificar
                <ArrowRight size={20} />
              </>
            )}
          </button>

          {/* Resend */}
          <div className="text-center">
            <button
              onClick={handleResend}
              disabled={!canResend || isSending}
              className={`text-sm ${
                canResend 
                  ? 'text-primary-600 hover:text-primary-700 font-medium'
                  : 'text-gray-400 cursor-not-allowed'
              } flex items-center justify-center gap-1 mx-auto`}
            >
              <RefreshCw size={16} className={isSending ? 'animate-spin' : ''} />
              {isSending ? 'Enviando...' : 'Reenviar código'}
            </button>
          </div>

          {/* Cancel */}
          <div className="text-center mt-4">
            <button
              onClick={onCancel}
              disabled={isVerifying || isSending}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Verificar más tarde
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
